import React, { useEffect, useState } from 'react';
import {
    GA_ACTION_FORM_DISMISSED,
    GA_CONTACT_ORGANIZER_CATEGORY,
    SUCCESS_VIEW,
} from '../constants';
import { gettext } from '../i18n';
import { trackAnalyticsEvent } from '../analytics';

const defaultConfig: OverlayConfigOptions = {
    title: gettext('Contact the organizer'),
};

export interface OverlayConfigOptions {
    title: string;
    onPrimaryAction?: (() => void) | null;
    primaryText?: string | null;
    primaryType?: string | null;
    onSecondaryAction?: (() => void) | null;
    secondaryText?: string | null;
    secondaryType?: string | null;
}

interface OverlayConfig {
    overlayConfig: OverlayConfigOptions;
    setOverlayConfig: (config: OverlayConfigOptions) => void;
}

interface SetVisibleArgs {
    value?: boolean;
    noAnalyticsTrack?: boolean;
}

interface OverlayVisible {
    isVisible: boolean;
    setVisible: (args?: SetVisibleArgs) => void;
    setCurrentView: (view: string) => void;
}

export const OverlayConfigContext = React.createContext<Partial<OverlayConfig>>(
    {},
);
export const OverlayVisibilityContext = React.createContext<
    Partial<OverlayVisible>
>({});

interface OverlayProviderProps {
    isVisible: boolean;
    onClose?: () => void;
    children?: React.ReactNode;
}

export const OverlayProvider: React.FunctionComponent<OverlayProviderProps> = (
    props,
) => {
    const [isVisible, setVisible] = useState(props.isVisible);
    const [currentView, setCurrentView] = useState<string | null>(null);
    const [overlayConfig, setOverlayConfig] = useState(defaultConfig);

    useEffect(() => {
        setVisible(props.isVisible);
    }, [props.isVisible]);

    const handleSetVisible = ({
        value = false,
        noAnalyticsTrack = false,
    }: SetVisibleArgs = {}) => {
        setVisible(value);

        if (!value && props.onClose) {
            props.onClose();
        }

        if (!value && currentView !== SUCCESS_VIEW && !noAnalyticsTrack) {
            trackAnalyticsEvent({
                action: GA_ACTION_FORM_DISMISSED,
                category: GA_CONTACT_ORGANIZER_CATEGORY,
                label: currentView ?? undefined,
            });
        }
    };

    return (
        <OverlayConfigContext.Provider
            value={{ overlayConfig, setOverlayConfig }}
        >
            <OverlayVisibilityContext.Provider
                value={{
                    isVisible,
                    setVisible: handleSetVisible,
                    setCurrentView: (view: string) => setCurrentView(view),
                }}
            >
                {props.children}
            </OverlayVisibilityContext.Provider>
        </OverlayConfigContext.Provider>
    );
};
